<template>
    <div>
        <el-dialog
            :visible.sync="showModal"
            width="600px"
            append-to-body
            :close-on-click-modal="false"
            @closed="resetForm"
            :destroy-on-close="true"
        >
            <template slot="title">
                <div class="d-flex align-items-center" :style="{ gap: '8px' }">
                    <el-tag class="rounded-circle">
                        <i class="el-icon-notebook-2"></i>
                    </el-tag>
                    Nova consulta SPC
                </div>
            </template>

            <el-form 
                :model="payload" 
                ref="spcConsultForm"
                label-position="top" 
                status-icon
            > 
                <el-row :gutter="12">
                    <!-- Tipo -->
                    <el-col>
                        <el-form-item
                            prop="tipo"
                            :rules="[
                                { required: true, message: 'Tipo de pessoa é obrigatório' },
                            ]"
                        >
                            <label>Tipo de pessoa</label>

                            <el-radio-group v-model="payload.tipo">
                                <el-radio-button label="F">Pessoa Física</el-radio-button>
                                <el-radio-button label="J">Pessoa Jurídica</el-radio-button>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>

                    <!-- CPF/CNPJ -->
                    <el-col>
                        <el-form-item 
                            v-if="payload.tipo == 'F'"
                            prop="documento"
                            :rules="[
                                { required: true, message: 'Documento obrigatório', trigger: 'blur' },
                                { validator: validateMin(11, 'CPF deve possuir 11 caracteres'), trigger: 'blur' },
                                { validator: validateMax(11), trigger: 'blur' },
                            ]"
                        >
                            <label>CPF</label>
                            <el-input
                                v-model="payload.documento" 
                                placeholder="Ex: ###.###.###-##"
                                :disabled="isLoading"
                                v-mask="['###.###.###-##']"
                                maxlength="14"
                            />
                        </el-form-item>

                        <el-form-item
                            v-if="payload.tipo == 'J'"
                            prop="documento"
                            :rules="[
                                { required: true, message: 'Documento obrigatório' },
                                { validator: validateMin(14, 'CNPJ deve possuir 14 caracteres'), trigger: 'blur' },
                                { validator: validateMax(14), trigger: 'blur' },
                            ]"
                        >
                            <label>CNPJ</label>
                            <el-input
                                v-model="payload.documento" 
                                placeholder="Ex: ##.###.###/####-##"
                                :disabled="isLoading"
                                v-mask="['##.###.###/####-##']"
                                maxlength="18"
                            />
                        </el-form-item>
                    </el-col>

                    <!-- Actions -->
                    <el-col>
                        <div class="d-flex align-items-center justify-content-end">
                            <el-form-item>
                                <el-button 
                                    plain
                                    :disabled="isLoading"
                                    @click="() => {
                                        toggleModal();
                                        resetForm();
                                    }"
                                >
                                    Cancelar
                                </el-button>

                                <el-button 
                                    type="primary"
                                    plain
                                    :disabled="isLoading"
                                    @click="onSubmit"
                                >
                                    <i v-if="isLoading" class="el-icon-loading"></i>
                                    <i class="fas fa-check-circle"></i>
                                    Consultar
                                </el-button>
                            </el-form-item>
                        </div>
                    </el-col>
                </el-row>
            </el-form>
        </el-dialog>
    </div>
</template>
<script>
    // Components
    import {
        Loading,
        Row,
        Col,
        Button,
        Input,
        Notification,
        RadioGroup,
        RadioButton,
        Form, 
        FormItem,
        Tag,
        Dialog,
    } from "element-ui";

    //Api
    import { SPCApi } from "@/api/SPCApi";
    const spcConsultasApi = new SPCApi();

    // Utils
    import { validateMin, validateMax } from "@/schemas/generalSchema"
    import { formatClear } from '@/util/formatters';

    export default {
        name: 'spc-create-modal',
        data() {
            return {
                payload: {
                    tipo: 'F',
                    documento: null,
                },
                showModal: false,
                isLoading: false,
            }
        }, 
        methods: {
            onSubmit() {
                this.$refs.spcConsultForm.validate( async (valid) => {
                    if (!valid) {
                        return false
                    } 

                    this.isLoading = true;
                    
                    try {
                        this.payload.documento = this.formatClear(this.payload.documento);

                        const res = await spcConsultasApi.store(this.payload)

                        Notification.success({
                            title: 'Sucesso',
                            message: res?.data?.msg ?? 'Consulta SPC criada com sucesso!'
                        });
                    } catch (error) {
                        if (error?.response?.status == 422) {
                            if (error?.response?.data.errors) {
                                Object.values(error.response.data.errors)
                                .flat()
                                .forEach(err_message => {
                                    Notification.warning({
                                        title: 'Atenção',
                                        message: err_message
                                    });
                                });
                            }
                            
                            return
                        }

                        Notification.error({
                            title: 'Erro',
                            message: 'Não foi possível criar uma consulta SPC!'
                        });
                    } finally {
                        this.isLoading = false;
                        this.toggleModal();
                        this.resetForm();
                        this.$emit('created');
                    }
                })
            },
            /**Modal */
            toggleModal() {
                this.showModal = !this.showModal;
            },
            resetForm() {
                if (this.$refs.spcConsultForm) {
                    this.$refs.spcConsultForm.resetFields();
                }
            },
            validateMin,
            validateMax,
            formatClear,
        },
        computed: {
            isAuthorized() {
                return this.$store.getters['user/isAnyRole']([
                    'adm',
                    'coord_comercial',
                    'gerente_regional'
                ]);
            },
        },
        watch: {
            "payload.tipo": {
                handler(_newValue, _oldValue) {
                    this.payload.documento = null;
                }
            }
        },
        components: {
            [Loading.name]: Loading,
            [Row.name]: Row,
            [Col.name]: Col,
            [Notification.name]: Notification,
            [Form.name]: Form,
            [FormItem.name]: FormItem,
            [Input.name]: Input,
            [Button.name]: Button,
            [Tag.name]: Tag,
            [Dialog.name]: Dialog,
            [RadioGroup.name]: RadioGroup,
            [RadioButton.name]: RadioButton,
        },
    }
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/_macro/spc/SPCCreateModal.vue